import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getLoanCreditReporting, previewCreditReportingSegment, saveLoanCreditReporting } from 'services'
import { Button, Input2, Select2, Toggle } from 'stories/components'
import { formatDate, thousandSeperatorWithPrefix } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import {
  ACCOUNT_STATUS_LABELS,
  ACCOUNT_TYPES,
  COMPLIANCE_CONDITION_CODES,
  CONSUMER_INFORMATION_INDICATORS,
  ECOA_CODES,
  HISTORY_CODE_LABELS,
  PORTFOLIO_TYPES,
  SPECIAL_COMMENTS,
} from './constants'

interface HistoryRow {
  id: number
  cycle: string
  asOfDate: string
  accountStatus: string
  paymentRating: string
  historyCode: string
  daysLate: number
  currentBalance: number
  amountPastDue: number
  scheduledMonthlyPayment: number
  actualPaymentAmount: number
  dateOfLastPayment: string
  dateOfFirstDelinquency: string
  reportedAt: string
  exceptions: Array<{ severity: string; field: string; message: string }>
}

/**
 * What this loan tells the credit bureaus.
 *
 * The month-by-month table is the point of the screen: it is the payment history as it was
 * furnished, not as today's ledger would recompute it, so a servicer answering a borrower's
 * dispute is looking at what the bureaus were actually told.
 */
export default function CreditReporting() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [data, setData] = useState<any>(null)
  const [settings, setSettings] = useState<Record<string, any>>({})
  const [segment, setSegment] = useState('')

  const { hasPermission } = usePermissions()
  const canEdit = hasPermission('Servicing.CreditReporting.Edit')

  const load = async () => {
    setLoading(true)
    const res = await getLoanCreditReporting().catch(() => null)
    if (res?.data) {
      setData(res.data)
      setSettings({ ...res.data.settings })
    }
    setLoading(false)
  }

  useEffect(() => {
    setLoanNumber()
    load()
  }, [])

  const onSave = async () => {
    setSaving(true)
    const res = await saveLoanCreditReporting(settings).catch(() => null)
    setSaving(false)
    if (!res?.success) return toast('Could not save the credit reporting settings.', { type: 'error' })
    toast('Credit reporting settings saved.', { type: 'success' })
    load()
  }

  const onPreview = async () => {
    const res = await previewCreditReportingSegment().catch(() => null)
    if (!res?.success) return toast(res?.message || 'Nothing to preview for this loan yet.', { type: 'info' })
    setSegment(res.segment || '')
  }

  const set = (key: string, value: any) => setSettings((prev) => ({ ...prev, [key]: value }))

  const history: HistoryRow[] = data?.history || []
  const blocking = useMemo(
    () => (data?.exceptions || []).filter((e: any) => e.severity === 'error'),
    [data?.exceptions],
  )
  const warnings = useMemo(
    () => (data?.exceptions || []).filter((e: any) => e.severity === 'warning'),
    [data?.exceptions],
  )

  if (loading) return <LayoutLoading show />

  return (
    <div className="credit-reporting-container relative">
      <LayoutLoading show={saving} />
      <div className="text-[16px] font-variation-settings-600 mb-2">Credit Reporting</div>
      <p className="text-sm text-gray-500 mb-4 max-w-3xl">
        What this loan reports to the consumer credit bureaus each month, and the two years of payment history behind
        it. Furnishing is off until it is turned on here — business-purpose loans are not consumer credit and do not
        belong on a consumer&rsquo;s file.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <Toggle
          id="creditReportingEnabled"
          title="Report this loan to the credit bureaus"
          value={settings.enabled === true}
          disabled={!canEdit}
          onChange={(value: boolean) => set('enabled', value)}
        />
      </div>

      {blocking.length > 0 && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 max-w-3xl">
          <p className="text-sm font-semibold text-red-800 flex items-center gap-1">
            <ExclamationTriangleIcon className="w-4 h-4" />
            This loan will be held out of the file until these are fixed
          </p>
          <ul className="mt-1 text-sm text-red-700 list-disc ml-5">
            {blocking.map((e: any, i: number) => (
              <li key={i}>
                <span className="font-semibold">{e.field}</span> — {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mb-4 p-3 rounded bg-amber-50 border border-amber-200 max-w-3xl">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1">
            <InformationCircleIcon className="w-4 h-4" />
            Reported, but thinner than it could be
          </p>
          <ul className="mt-1 text-sm text-amber-700 list-disc ml-5">
            {warnings.map((e: any, i: number) => (
              <li key={i}>
                <span className="font-semibold">{e.field}</span> — {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 max-w-5xl mb-6">
        <Select2
          id="portfolioType"
          title="Portfolio Type"
          options={PORTFOLIO_TYPES}
          value={settings.portfolioType || ''}
          disabled={!canEdit}
          hasDefaultOption
          defaultOptionText={`Tenant default (${data?.effective?.portfolioType || 'M'})`}
          onChange={(value: string) => set('portfolioType', value)}
        />
        <Select2
          id="accountType"
          title="Account Type"
          options={ACCOUNT_TYPES}
          value={settings.accountType || ''}
          disabled={!canEdit}
          hasDefaultOption
          defaultOptionText={`Tenant default (${data?.effective?.accountType || '26'})`}
          onChange={(value: string) => set('accountType', value)}
        />
        <Select2
          id="ecoaCode"
          title="ECOA Code"
          options={ECOA_CODES}
          value={settings.ecoaCode || ''}
          disabled={!canEdit}
          hasDefaultOption
          defaultOptionText="Derived from the borrowers"
          onChange={(value: string) => set('ecoaCode', value)}
        />
        <Select2
          id="accountStatusOverride"
          title="Account Status override"
          options={ACCOUNT_STATUS_LABELS}
          value={settings.accountStatusOverride || ''}
          disabled={!canEdit}
          hasDefaultOption
          defaultOptionText="Derived from how late the loan is"
          onChange={(value: string) => set('accountStatusOverride', value)}
        />
        <Select2
          id="specialComment"
          title="Special Comment"
          options={SPECIAL_COMMENTS}
          value={settings.specialComment || ''}
          disabled={!canEdit}
          hasDefaultOption
          defaultOptionText="None"
          onChange={(value: string) => set('specialComment', value)}
        />
        <Select2
          id="complianceConditionCode"
          title="Compliance Condition Code"
          options={COMPLIANCE_CONDITION_CODES}
          value={settings.complianceConditionCode || ''}
          disabled={!canEdit}
          hasDefaultOption
          defaultOptionText="None"
          onChange={(value: string) => set('complianceConditionCode', value)}
        />
        <Select2
          id="consumerInformationIndicator"
          title="Consumer Information Indicator"
          options={CONSUMER_INFORMATION_INDICATORS}
          value={settings.consumerInformationIndicator || ''}
          disabled={!canEdit}
          hasDefaultOption
          defaultOptionText="None"
          onChange={(value: string) => set('consumerInformationIndicator', value)}
        />
        <Input2
          type="date"
          title="Date of First Delinquency"
          value={settings.dateOfFirstDelinquency || ''}
          disabled={!canEdit}
          tooltip="Starts the seven-year clock. Stamped automatically the month the loan first goes 30 days down, and cleared when it comes current. Set it by hand for a loan that arrived already delinquent."
          onChange={(value: string) => set('dateOfFirstDelinquency', value)}
        />
        <Input2
          type="date"
          title="Date Closed"
          value={settings.dateClosed || ''}
          disabled={!canEdit}
          onChange={(value: string) => set('dateClosed', value)}
        />
        <Input2
          title="Account Number override"
          value={settings.accountNumberOverride || ''}
          disabled={!canEdit}
          tooltip={`Reported now as ${data?.effective?.consumerAccountNumber || '(none)'}`}
          onChange={(value: string) => set('accountNumberOverride', value)}
        />
        <Input2
          type="date"
          title="Date Opened override"
          value={settings.dateOpenedOverride || ''}
          disabled={!canEdit}
          tooltip={`Reported now as ${data?.effective?.dateOpened || '(none)'}`}
          onChange={(value: string) => set('dateOpenedOverride', value)}
        />
        <Input2
          title="Notes"
          value={settings.notes || ''}
          disabled={!canEdit}
          onChange={(value: string) => set('notes', value)}
        />
      </div>

      <div className="flex gap-2 mb-8">
        {canEdit && (
          <Button onClick={onSave} loading={saving}>
            Save
          </Button>
        )}
        <Button color="gray" onClick={onPreview}>
          Preview Metro 2 record
        </Button>
      </div>

      {segment && (
        <div className="mb-8">
          <p className="font-semibold text-[15px]">Base segment as it would be sent</p>
          <p className="text-sm text-gray-500 mb-1">
            Fixed-width, {segment.length} bytes. This is the record itself, not a rendering of it.
          </p>
          <pre className="text-[11px] bg-gray-50 border rounded p-3 overflow-x-auto whitespace-pre">{segment}</pre>
        </div>
      )}

      <div>
        <p className="font-semibold text-[15px]">Payment history</p>
        <p className="text-sm text-gray-500 mb-2">
          Profile as of today, most recent month first:{' '}
          <span className="font-mono tracking-widest">{data?.profile || ''}</span>
        </p>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">
            Nothing has been snapshotted for this loan yet. Cycles are taken monthly from the Credit Reporting screen.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-[14px] text-left text-gray-900 w-full">
              <thead className="text-gray-600 text-[13px] border-b">
                <tr>
                  <th className="pr-6 py-1">Cycle</th>
                  <th className="pr-6 py-1">As of</th>
                  <th className="pr-6 py-1">Status</th>
                  <th className="pr-6 py-1">History</th>
                  <th className="pr-6 py-1 text-right">Days late</th>
                  <th className="pr-6 py-1 text-right">Balance</th>
                  <th className="pr-6 py-1 text-right">Past due</th>
                  <th className="pr-6 py-1 text-right">Paid</th>
                  <th className="pr-6 py-1">Last payment</th>
                  <th className="py-1">Furnished</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => {
                  const errors = (row.exceptions || []).filter((e) => e.severity === 'error')
                  return (
                    <tr key={row.id} className="border-b">
                      <td className="pr-6 py-1 font-semibold">{row.cycle}</td>
                      <td className="pr-6 py-1 text-gray-500">{formatDate(row.asOfDate)}</td>
                      <td className="pr-6 py-1">
                        {row.accountStatus}
                        <span className="text-gray-500"> {ACCOUNT_STATUS_LABELS[row.accountStatus] || ''}</span>
                        {row.paymentRating ? (
                          <span className="text-gray-500"> · rating {row.paymentRating}</span>
                        ) : null}
                      </td>
                      <td className="pr-6 py-1">
                        <span className="font-mono">{row.historyCode}</span>
                        <span className="text-gray-500"> {HISTORY_CODE_LABELS[row.historyCode] || ''}</span>
                      </td>
                      <td className="pr-6 py-1 text-right">{row.daysLate || 0}</td>
                      <td className="pr-6 py-1 text-right">{thousandSeperatorWithPrefix(row.currentBalance)}</td>
                      <td className="pr-6 py-1 text-right">{thousandSeperatorWithPrefix(row.amountPastDue)}</td>
                      <td className="pr-6 py-1 text-right">{thousandSeperatorWithPrefix(row.actualPaymentAmount)}</td>
                      <td className="pr-6 py-1 text-gray-500">
                        {row.dateOfLastPayment ? formatDate(row.dateOfLastPayment) : '—'}
                      </td>
                      <td className="py-1">
                        {row.reportedAt ? (
                          <span className="text-green-700">{formatDate(row.reportedAt)}</span>
                        ) : errors.length ? (
                          <span className="text-red-700" title={errors.map((e) => e.message).join('\n')}>
                            Held back
                          </span>
                        ) : (
                          <span className="text-gray-500">Draft</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
